import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Row, Col, InputGroup, Container } from 'react-bootstrap';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import { postDatatoServer } from '../../utils/apiBase';
import ContextHelper from '../../ContextHooks/ContextHelper';
import { FormField } from '../formFields/FormField';
import { useLocation, useNavigate } from 'react-router-dom';

const allowedHeadersOptions = [
  'TAT',
  'Location',
  'Name',
  'PatientID',
  'Study',
  'Report',
  'History',
  'Study date',
  'Download',
  'Upload Date',
  'Status',
  'Images',
  'HistoryIMG',
  'Viewer',
  'Modality',
  'Assigned To',
  'Share Report',
  'Source',
];

const allowedHeadersOptions1 = allowedHeadersOptions.map((header) => ({
  value: header,
  label: header,
}));

const VerifierUser = () => {
  const { currentUser } = ContextHelper();
  const { state } = useLocation();
  const navigate = useNavigate();
  const selectedUser = state?.selectedUser;

  const initialState = {
    username: '',
    type: 'verifier',
    isadmin: true,
    password: '',
    address: '',
    email: '',
    permissions: {
      add_study: 'false',
      patient_billing: 'false',
      patient_registration: 'false',
      business_revenue: 'false',
      turnaround_time: 'false',
      tag_update: 'false',
    },
    allowedHeader: [],
    doctorVerifiedImage: null,
    doctorUnverifiedImage: null,
  };

  const [formData, setFormData] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [fileErrors, setFileErrors] = useState({
    doctorVerified: '',
    doctorUnverified: '',
  });
  const [touched, setTouched] = useState({});

  const doctorVerifiedRef = useRef(null);
  const doctorUnverifiedRef = useRef(null);

  const validateFile = (file, type) => {
    const allowedTypes = ['image/jpeg'];
    const maxSize = 5 * 1024 * 1024;

    if (!file) {
      return `${type} image is required`;
    }
    if (!allowedTypes.includes(file.type)) {
      return 'Only JPEG images are allowed';
    }
    if (file.size > maxSize) {
      return 'File size should not exceed 5MB';
    }
    return '';
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const renamedFile = new File([file], `${formData.username || 'user'}_${type}.jpg`, { type: file.type });
      const validationError = validateFile(renamedFile, type);

      if (type === 'doctorVerified') {
        setFormData((prev) => ({ ...prev, doctorVerifiedImage: validationError ? null : renamedFile }));
        setFileErrors((prev) => ({ ...prev, doctorVerified: validationError }));
        if (validationError) doctorVerifiedRef.current.value = '';
      } else if (type === 'doctorUnverified') {
        setFormData((prev) => ({ ...prev, doctorUnverifiedImage: validationError ? null : renamedFile }));
        setFileErrors((prev) => ({ ...prev, doctorUnverified: validationError }));
        if (validationError) doctorUnverifiedRef.current.value = '';
      }

      if (validationError) {
        toast.error(`${type} image error: ${validationError}`);
      }
    }
  };

  useEffect(() => {
    if (selectedUser) {
      let parsedPermissions = initialState.permissions;
      try {
        if (typeof selectedUser.permissions === 'string') {
          parsedPermissions = JSON.parse(selectedUser.permissions);
        } else if (typeof selectedUser.permissions === 'object') {
          parsedPermissions = selectedUser.permissions;
        }
      } catch (err) {
        console.warn('Invalid permissions format:', selectedUser.permissions);
      }

      setFormData({
        ...initialState,
        ...selectedUser,
        permissions: parsedPermissions,
        allowedHeader: selectedUser.allowedHeader || [],
      });
    }
  }, [selectedUser]);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('permissions.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [key]: checked ? 'true' : 'false',
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'username':
        if (!value?.trim()) error = 'Username is required';
        break;
      case 'email':
        if (!value?.trim()) {
          error = 'Email is required';
        } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'password':
        if (!value?.trim()) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        }
        break;
      case 'address':
        if (!value?.trim()) error = 'Address is required';
        break;
      case 'allowedHeader':
        if (Array.isArray(value) && value.length === 0) {
          error = 'At least one header must be selected';
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    return error;
  };

  const handlePermissionChange = (permission, checked) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: checked ? 'true' : 'false',
      },
    }));
  };

  const handleAllowedHeaderChange = (selectedOptions) => {
    const newHeaders = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];

    setFormData((prev) => ({
      ...prev,
      allowedHeader: newHeaders,
    }));

    if (newHeaders.length === 0) {
      setErrors((prev) => ({
        ...prev,
        allowedHeader: 'At least one header must be selected',
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        allowedHeader: '',
      }));
    }
  };

  const handleResponse = (res) => {
    if (res?.response && res?.status === 'success') {
      if (
        res.response?.message === 'duplicate username,already available' ||
        res.response?.message === 'user with this email, already available'
      ) {
        toast.error(res.response.message);
      } else {
        toast.success(selectedUser ? 'User updated successfully' : 'User created successfully');
        setFormData(initialState);
        navigate('/verifier');
      }
    } else {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    const allFields = ['username', 'email', 'password', 'address'];

    const newTouched = {};
    allFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    allFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        validationErrors[field] = error;
      }
    });

    const verifiedImageError =
      !selectedUser && !formData.doctorVerifiedImage ? 'Doctor Verified Image is required' : '';
    const unverifiedImageError =
      !selectedUser && !formData.doctorUnverifiedImage ? 'Doctor Unverified Image is required' : '';

    if (verifiedImageError) {
      validationErrors.doctorVerifiedImage = verifiedImageError;
      setFileErrors((prev) => ({ ...prev, doctorVerified: verifiedImageError }));
    }

    if (unverifiedImageError) {
      validationErrors.doctorUnverifiedImage = unverifiedImageError;
      setFileErrors((prev) => ({ ...prev, doctorUnverified: unverifiedImageError }));
    }

    if (formData.allowedHeader.length === 0) {
      validationErrors.allowedHeader = 'At least one header must be selected';
    }

    setErrors((prev) => ({ ...prev, ...validationErrors }));
    return Object.keys(validationErrors).length === 0;
  };

  const clearFiles = () => {
    setFormData((prev) => ({
      ...prev,
      doctorVerifiedImage: null,
      doctorUnverifiedImage: null,
    }));

    if (doctorVerifiedRef.current) doctorVerifiedRef.current.value = '';
    if (doctorUnverifiedRef.current) doctorUnverifiedRef.current.value = '';

    setFileErrors({
      doctorVerified: '',
      doctorUnverified: '',
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    const body = new FormData();
    body.append('username', formData.username);
    body.append('type', 'verifier');
    body.append('isadmin', formData.isadmin ? 'true' : 'false');
    body.append('password', formData.password);
    body.append('address', formData.address);
    body.append('email', formData.email);
    body.append('permissions', JSON.stringify(formData.permissions));
    body.append('allowedHeader', JSON.stringify(formData.allowedHeader));

    if (formData.doctorVerifiedImage) {
      body.append('files', formData.doctorVerifiedImage);
    }

    if (formData.doctorUnverifiedImage) {
      body.append('files', formData.doctorUnverifiedImage);
    }

    if (selectedUser) {
      body.append('update', 'true');
    }

    postDatatoServer({
      end_point: 'user2',
      body: body,
      call_back: handleResponse,
      props: {
        header: true,
        token: currentUser?.token,
        contentType: 'multipart/form-data',
      },
    });
  };

  const shouldShowError = (fieldName) => {
    return touched[fieldName] && errors[fieldName];
  };

  return (
    <section className="bg-white p-4 min-vh-100 d-flex justify-content-center">
      <Container fluid className="px-0">
        <Row className="justify-content-center">
          <Col>
            <div className="d-flex justify-content-between">
              <h3 className="base-color mb-4">{selectedUser ? 'Edit Verifier' : 'Create Verifier'}</h3>
            </div>
            <Form onSubmit={handleFormSubmit} noValidate>
              <Row className="mb-3">
                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Username <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      value={formData?.username?.replace(/\s+/g, '')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={shouldShowError('username')}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group controlId="type">
                    <Form.Label>Type</Form.Label>
                    <Form.Control type="text" value="verifier" disabled readOnly />
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData?.email?.replace(/\s+/g, '')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={shouldShowError('email')}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Password <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData?.password?.replace(/\s+/g, '')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={shouldShowError('password')}
                        required
                      />
                      <Button variant="outline-secondary" onClick={handleTogglePassword}>
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </Button>
                      <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Address <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={shouldShowError('address')}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3" controlId="allowedHeader">
                    <Form.Label>
                      Allowed Headers <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      isMulti
                      options={allowedHeadersOptions1}
                      value={allowedHeadersOptions1.filter((opt) => formData.allowedHeader.includes(opt.value))}
                      onChange={handleAllowedHeaderChange}
                      placeholder="Select allowed headers"
                      className={errors.allowedHeader ? 'is-invalid' : ''}
                    />
                    {errors.allowedHeader && <div className="invalid-feedback d-block">{errors.allowedHeader}</div>}
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Doctor Verified Image <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      ref={doctorVerifiedRef}
                      type="file"
                      onChange={(e) => handleFileChange(e, 'doctorVerified')}
                      accept="image/jpeg"
                      isInvalid={!!fileErrors.doctorVerified}
                      required={!selectedUser}
                    />
                    <Form.Control.Feedback type="invalid">{fileErrors.doctorVerified}</Form.Control.Feedback>
                    {formData.doctorVerifiedImage && (
                      <small className="text-muted d-block mt-1">
                        Selected file: {formData.doctorVerifiedImage.name}
                      </small>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3} md={4} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Doctor Unverified Image <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      ref={doctorUnverifiedRef}
                      type="file"
                      onChange={(e) => handleFileChange(e, 'doctorUnverified')}
                      accept="image/jpeg"
                      isInvalid={!!fileErrors.doctorUnverified}
                      required={!selectedUser}
                    />
                    <Form.Control.Feedback type="invalid">{fileErrors.doctorUnverified}</Form.Control.Feedback>
                    {formData.doctorUnverifiedImage && (
                      <small className="text-muted d-block mt-1">
                        Selected file: {formData.doctorUnverifiedImage.name}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="mb-3">
                <h5 className="fw-bold mb-2">Module Permissions</h5>
                <Row className="gy-3">
                  {Object.entries(formData.permissions).map(([key, value]) => (
                    <Col xs={12} sm={6} md={4} lg={3} key={key}>
                      <Form.Check
                        type="switch"
                        id={`perm-${key}`}
                        name={`permissions.${key}`}
                        label={key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        checked={value}
                        onChange={handleChange}
                        className="text-nowrap fs-16"
                      />
                    </Col>
                  ))}
                </Row>
              </div>

              <div className="d-flex align-items-center">
                <Button type="submit" className="login-btn fs-15 base-color-bg text-white fw-semibold">
                  {selectedUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default VerifierUser;
